import React from 'react';
import { Link } from 'react-router-dom';
import MarketingLayout from '../../components/MarketingLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { 
  ArrowRight, CreditCard, RefreshCw, DollarSign, 
  Fuel, Users, Wrench, Leaf, CheckCircle
} from 'lucide-react';

const WorkingCapitalPage = () => {
  const operationalExpenses = [
    { icon: Users, title: "Payroll", description: "Keep your crews paid on time, every time" },
    { icon: Fuel, title: "Fuel", description: "Cover fuel costs for your fleet" },
    { icon: Leaf, title: "Materials", description: "Purchase supplies and materials when needed" },
    { icon: Wrench, title: "Maintenance", description: "Keep equipment in top condition" },
  ];

  const benefits = [
    "Draw funds as needed, only pay for what you use",
    "Cover short-term expenses without depleting cash reserves",
    "Repay and reuse capital throughout the year",
    "Manage seasonal fluctuations with ease",
    "No need to reapply—access funds when you need them",
    "Flexible terms designed for landscaping businesses"
  ];

  return (
    <MarketingLayout>
      {/* Hero */}
      <section className="pt-16 pb-20 bg-gradient-to-b from-emerald-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <CreditCard className="h-4 w-4" />
                Working Capital
              </div>
              <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 mb-6">
                Working Capital Line of Credit
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                Flexible revolving credit designed for the daily operational needs of landscaping companies. Draw funds as needed, repay, and reuse—keeping your business running smoothly year-round.
              </p>
              <Link to="/apply">
                <Button size="lg" className="bg-primary hover:bg-primary/90">
                  Apply Now <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
            <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center">
                  <RefreshCw className="h-8 w-8 text-emerald-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Revolving Credit</h3>
                  <p className="text-gray-500">Use it, repay it, use it again</p>
                </div>
              </div>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <span className="text-gray-600">Available Credit</span>
                  <span className="text-xl font-bold text-emerald-600">$50,000</span>
                </div>
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <span className="text-gray-600">Current Balance</span>
                  <span className="text-xl font-bold text-gray-900">$12,500</span>
                </div>
                <div className="flex items-center justify-between p-4 bg-emerald-50 rounded-lg">
                  <span className="text-emerald-700">Available to Draw</span>
                  <span className="text-xl font-bold text-emerald-700">$37,500</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The Challenge */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">The Reality of Running a Landscaping Business</h2>
            <p className="text-lg text-gray-600">
              Landscaping companies face ongoing operational expenses—payroll, fuel, materials, maintenance—and seasonal fluctuations that can strain cash flow. A working capital line of credit provides the flexibility to handle these challenges.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {operationalExpenses.map((expense, index) => (
              <Card key={index} className="border-gray-100 text-center">
                <CardContent className="pt-8 pb-8">
                  <div className="w-14 h-14 bg-gray-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <expense.icon className="h-7 w-7 text-gray-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{expense.title}</h3>
                  <p className="text-gray-600 text-sm">{expense.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">How It Works</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              A revolving line of credit that works the way your business does.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="w-16 h-16 bg-primary text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                1
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Draw Funds</h3>
              <p className="text-gray-600">Access your credit line whenever you need capital for operations, materials, or unexpected expenses.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-primary text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                2
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Cover Expenses</h3>
              <p className="text-gray-600">Use the funds for payroll, fuel, materials, equipment maintenance, or any operational need.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-primary text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                3
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Repay & Reuse</h3>
              <p className="text-gray-600">As you repay, your available credit replenishes. No need to reapply—the capital is there when you need it.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Flexible Working Capital for Daily Operations
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Unlike traditional loans with fixed disbursements, a line of credit lets you access exactly what you need, when you need it. It's the financial flexibility landscaping businesses deserve.
              </p>
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckCircle className="h-4 w-4 text-emerald-600" />
                    </div>
                    <span className="text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-emerald-50 rounded-2xl p-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Perfect For:</h3>
              <ul className="space-y-3">
                <li className="flex items-center gap-3">
                  <DollarSign className="h-5 w-5 text-emerald-600" />
                  <span className="text-gray-700">Bridging gaps between customer payments</span>
                </li>
                <li className="flex items-center gap-3">
                  <DollarSign className="h-5 w-5 text-emerald-600" />
                  <span className="text-gray-700">Covering payroll during slow seasons</span>
                </li>
                <li className="flex items-center gap-3">
                  <DollarSign className="h-5 w-5 text-emerald-600" />
                  <span className="text-gray-700">Stocking up on materials before busy periods</span>
                </li>
                <li className="flex items-center gap-3">
                  <DollarSign className="h-5 w-5 text-emerald-600" />
                  <span className="text-gray-700">Handling unexpected equipment repairs</span>
                </li>
                <li className="flex items-center gap-3">
                  <DollarSign className="h-5 w-5 text-emerald-600" />
                  <span className="text-gray-700">Taking on new contracts with upfront costs</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-primary">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">
            Get the Working Capital You Need
          </h2>
          <p className="text-xl text-white/80 mb-8">
            Apply today and access flexible funding for your landscaping business operations.
          </p>
          <Link to="/apply">
            <Button size="lg" className="bg-white text-primary hover:bg-gray-100">
              Apply Now <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </MarketingLayout>
  );
};

export default WorkingCapitalPage;
